/**
 * The map's search box.
 *
 * Replaces the bare MapTiler geocoder, which could only find place names. It
 * now answers whatever you have in your hand: a tenement ID, an A-Number, a
 * company sample or drill-hole ID, a holder's name, or a town. `searchSources`
 * decides which services to ask from the shape of what you typed.
 *
 * Search runs on submit, not per keystroke. The dataset lookups hit a
 * viewport-limited GeoServer and a holder-name ILIKE is a table scan; running
 * that on every character is how you take the map down for everyone.
 */

import { useEffect, useRef, useState } from 'react';
import { useMap } from '@vis.gl/react-maplibre';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faXmark, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { runSearch } from './searchSources';
import { SearchResultsModal } from './SearchResultsModal';

const s = {
  form: {
    display: 'flex', alignItems: 'center', gap: 6,
    width: 270, height: 40, padding: '0 6px 0 12px',
    background: 'rgba(255,255,255,0.97)',
    border: '1px solid var(--gf-border-color, #E5E7EB)',
    borderRadius: 10,
    boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
    fontFamily: 'var(--gf-font-sans, "Poppins", sans-serif)',
  },
  icon: { color: 'var(--gf-secondary-400, #6B7280)', flex: 'none' },
  input: {
    flex: 1, minWidth: 0, border: 0, outline: 'none', background: 'transparent',
    fontSize: 13, fontFamily: 'inherit', color: 'var(--gf-secondary-800, #1F2327)',
  },
  clear: {
    border: 0, background: 'none', cursor: 'pointer', padding: '0 4px', flex: 'none',
    color: 'var(--gf-secondary-400, #6B7280)',
  },
  go: {
    flex: 'none', width: 28, height: 28, borderRadius: 7, border: 0, cursor: 'pointer',
    background: 'var(--gf-primary-400, #FD7218)', color: '#FFFFFF',
  },
  hint: {
    marginTop: 6, padding: '6px 10px', maxWidth: 270,
    background: 'rgba(255,255,255,0.97)', borderRadius: 8,
    boxShadow: '0 2px 10px rgba(0,0,0,0.14)',
    fontFamily: 'var(--gf-font-sans, "Poppins", sans-serif)',
    fontSize: 11, lineHeight: 1.45, color: 'var(--gf-secondary-500, #4B5563)',
  },
};

export function MapSearch({ apiKey, onPick, onArrive }) {
  // Rendered inside <Map id="goldfindermap">, so the instance comes from
  // context rather than a ref threaded down from the parent.
  const { goldfindermap } = useMap();
  const map = goldfindermap;
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null);   // { query, results }
  const [notice, setNotice] = useState('');
  const abortRef = useRef(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  /**
   * Frames a result. A bbox (tenement outline, whole A-Number) is fitted so you
   * see its extent; a single point gets a zoom that still shows its
   * surroundings rather than dropping you on a featureless close-up.
   */
  const goTo = (result, onSettled) => {
    if (!map) return;

    // Fire once the camera stops. A popup opened at the same instant as the
    // flight would slide across the screen with the map for the whole
    // animation; opening it on arrival is what makes it read as "here it is".
    // `once` rather than `on` so a search that moves nothing cannot leave a
    // listener behind, and a timer backstops the case where the camera is
    // already where it needs to be and moveend never fires.
    if (onSettled) {
      let done = false;
      const settle = () => { if (!done) { done = true; onSettled(); } };
      map.once('moveend', settle);
      setTimeout(settle, 1200);
    }

    if (result.bbox) {
      const [w, sth, e, n] = result.bbox;
      // A degenerate bbox (one sample, or every sample stacked) would make
      // fitBounds zoom to maximum, so fall back to a point fly-to.
      if (Math.abs(e - w) > 1e-6 || Math.abs(n - sth) > 1e-6) {
        map.fitBounds([[w, sth], [e, n]], { padding: 80, maxZoom: 15, duration: 900 });
        return;
      }
    }
    if (result.lngLat) {
      map.flyTo({ center: [result.lngLat.lng, result.lngLat.lat], zoom: 14, duration: 900 });
    }
  };

  const pick = (result) => {
    setModal(null);
    // Layer toggles and feature loading happen immediately, so the data is on
    // its way while the camera is still moving. Only the popup waits.
    onPick?.(result);
    goTo(result, onArrive ? () => onArrive(result) : undefined);
  };

  const submit = async (e) => {
    e?.preventDefault();
    const input = query.trim();
    if (!input || busy) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setBusy(true);
    setNotice('');
    try {
      const { results, skipped } = await runSearch(input, {
        signal: controller.signal, apiKey,
      });
      if (controller.signal.aborted) return;

      if (results.length === 0) {
        setNotice(
          skipped.length
            ? 'No match. Sample and hole ID search is off until the database index is built - A-Numbers, tenements and places all work.'
            : 'No match. Try a tenement ID, an A-Number, or a place.'
        );
      } else if (results.length === 1) {
        // Nothing to choose between - just go.
        pick(results[0]);
      } else {
        setModal({ query: input, results });
      }
    } catch (err) {
      if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
        console.error('Search failed:', err);
        setNotice('Search failed. Check your connection and try again.');
      }
    } finally {
      if (!controller.signal.aborted) setBusy(false);
    }
  };

  return (
    <>
      <form style={s.form} onSubmit={submit} role="search">
        <FontAwesomeIcon icon={faMagnifyingGlass} size="sm" style={s.icon} />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setNotice(''); }}
          placeholder="Tenement, A-Number, sample, place"
          aria-label="Search tenements, samples, drill holes and places"
          style={s.input}
        />
        {query && (
          <button
            type="button"
            style={s.clear}
            onClick={() => { setQuery(''); setNotice(''); }}
            aria-label="Clear search"
          >
            <FontAwesomeIcon icon={faXmark} size="sm" />
          </button>
        )}
        <button type="submit" style={s.go} disabled={busy} aria-label="Search">
          <FontAwesomeIcon icon={busy ? faSpinner : faMagnifyingGlass} spin={busy} size="sm" />
        </button>
      </form>

      {notice && <div style={s.hint}>{notice}</div>}

      {modal && (
        <SearchResultsModal
          query={modal.query}
          results={modal.results}
          onPick={pick}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
}

export default MapSearch;
